import styled from 'styled-components';
import Dropzone from 'react-dropzone';

import { PrimaryButton } from 'common/components/molecules/ListToolbar';

import colors from 'common/styles/colors';
import { getPartnerBadgeStyles, getPartnerSearchInputBorder } from 'common/styles/organisationPartnerTheme';
import { scrollbar } from 'common/styles/scrollbar';
import {
  INK, INK_2, INK_3, LINE, LINE_2, BG, BLUE, BLUE_T, BLUE_LINE, CRITICAL, RADIUS, TYPE
} from 'common/styles/listUI';

// Accent tint and border from the brand theme, so a partner (NTN) theme never
// shows Petasense green around its own primary colour.
const accentTint = theme => getPartnerBadgeStyles(theme).background;
const accentBorder = theme => getPartnerSearchInputBorder(theme);

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: ${TYPE.CELL_SIZE};
  color: ${INK};
`;

export const Muted = styled.div`
  color: ${INK_2};
  font-size: 12px;
  line-height: 1.5;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${TYPE.HEADER_SIZE};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${INK_3};
`;

export const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: ${props => props.theme.primaryColor};
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;

export const ColumnCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: none;
  letter-spacing: 0;
  font-size: ${TYPE.CELL_SIZE};
  color: ${INK};
`;

// Keeps the table's header type, with the select-all box beside the label.
export const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const LockedHint = styled.span`
  margin-left: 6px;
  font-size: 11px;
  color: ${INK_3};
`;

export const EditableValue = styled.span`
  font-weight: ${props => (props.editable ? 600 : 400)};
  color: ${props => (props.editable ? INK : INK_3)};
`;

export const ScopeOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border-radius: ${RADIUS.menu};
  font-family: inherit;
  font-size: ${TYPE.CELL_SIZE};
  color: ${INK};
  text-align: left;
  cursor: pointer;
  border: 1px solid ${props => (props.selected ? accentBorder(props.theme) : LINE)};
  background: ${props => (props.selected ? accentTint(props.theme) : colors.white)};

  b { font-weight: 600; }
`;

export const ScopeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const WarnPill = styled.span`
  margin-left: 6px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: ${RADIUS.pill};
  white-space: nowrap;
  background: ${colors.warningBackground};
  color: ${colors.warning};
  border: 1px solid ${colors.warningYellow};
`;

export const ScopeDetail = styled.span`
  color: ${INK_2};
`;

export const ScopeCount = styled.span`
  margin-left: auto;
  color: ${INK_2};
  white-space: nowrap;
`;

export const Radio = styled.span`
  flex: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-sizing: border-box;
  border: ${props => (props.selected ? `4px solid ${props.theme.primaryColor}` : `1px solid ${INK_3}`)};
`;

export const Callout = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: ${RADIUS.menu};
  font-size: 12px;
  line-height: 1.5;
  color: ${INK};
  background: ${props => ({
    crit: colors.errorBackground, good: accentTint(props.theme), warn: colors.warningBackground
  }[props.tone] || BLUE_T)};
  border: 1px solid ${props => ({
    crit: colors.diffRed, good: accentBorder(props.theme), warn: colors.warningYellow
  }[props.tone] || BLUE_LINE)};

  svg {
    flex: none;
    margin-top: 2px;
    color: ${props => ({
    crit: CRITICAL, good: props.theme.primaryColor, warn: colors.warning
  }[props.tone] || BLUE)};
  }
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 14px;
  margin-top: 4px;
  border-top: 1px solid ${LINE};
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Steps = styled.ol`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
`;

export const Step = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${(props) => {
    if (props.current) return INK;
    return props.done ? props.theme.primaryColor : INK_3;
  }};
`;

export const StepNumber = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  background: ${(props) => {
    if (props.current) return INK;
    return props.done ? accentTint(props.theme) : LINE_2;
  }};
  color: ${(props) => {
    if (props.current) return colors.white;
    return props.done ? props.theme.primaryColor : INK_3;
  }};
`;

export const StepArrow = styled.span`
  color: ${INK_3};
  svg { width: 9px; height: 9px; }
`;

export const DropArea = styled(Dropzone)`
  border: 1.5px dashed ${props => (props.disabled ? LINE : accentBorder(props.theme))};
  background: ${BG};
  border-radius: ${RADIUS.table};
  padding: 34px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  color: ${INK_2};
  cursor: pointer;

  svg { width: 26px; height: 26px; color: ${props => props.theme.primaryColor}; }
`;

export const DropTitle = styled.b`
  color: ${INK};
  font-size: 13px;
`;

export const TableScroll = styled.div`
  max-height: 46vh;
  overflow: auto;
  border-radius: ${RADIUS.table};
  ${scrollbar}
`;

export const Pill = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: ${RADIUS.pill};
  white-space: nowrap;
  background: ${LINE_2};
  color: ${INK_2};
  border: 1px solid ${LINE};
`;

export const OldValue = styled.span`
  color: ${INK_2};
  text-decoration: line-through;
`;

export const NewValue = styled.span`
  color: ${INK};
  font-weight: 700;
`;

export const EmptyValue = styled.span`
  color: ${INK_3};
  font-style: italic;
`;

export const ClearedValue = styled.span`
  color: ${CRITICAL};
  font-style: italic;
`;

export const Stats = styled.div`
  display: flex;
  gap: 8px;
`;

export const Stat = styled.div`
  flex: 1;
  border: 1px solid ${LINE};
  border-radius: ${RADIUS.table};
  padding: 10px 12px;
  color: ${INK_2};
  font-size: 12px;
`;

export const StatValue = styled.b`
  display: block;
  font-size: 18px;
  color: ${props => (props.accent ? props.theme.primaryColor : INK)};
`;

export const FilterPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const FilterPill = styled.button`
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: ${RADIUS.pill};
  cursor: pointer;
  background: ${props => (props.selected ? INK : colors.white)};
  color: ${props => (props.selected ? colors.white : INK_2)};
  border: 1px solid ${props => (props.selected ? INK : LINE)};
`;

export const ArrowCell = styled.span`
  color: ${INK_3};
  svg { width: 10px; height: 10px; }
`;

// The shared PrimaryButton is a bare <button> with no disabled look.
export const PrimaryAction = styled(PrimaryButton)`
  &:disabled {
    opacity: 0.6;
    cursor: default;
    filter: none;
  }
`;
